/**
 * ListItem — Interactive row used as the building block for Menu items, Combobox
 * items, and custom action lists.
 *
 * ListItem Component Guidelines
 * - ListItem is the shared foundation for MenuItem and ComboboxItem. Use it directly
 *   only when building custom lists that need the same interaction patterns outside
 *   of a Menu or Combobox.
 * - Use `variant="destructive"` for dangerous actions (delete, remove) — it applies
 *   red text and hover state.
 * - Use `size="lg"` when items need a `description` subtitle — descriptions are only
 *   rendered at the `lg` size.
 * - Use `startEnhancer` for icons or avatars and `endEnhancer` for trailing actions,
 *   shortcuts, or indicators.
 * - Set `inset` to align items that don't have a `startEnhancer` with siblings that do.
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import * as React from "react";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";

const listItemVariants = cva(
  // Combobox keeps focus on the input; Base UI marks the active row with
  // data-highlighted instead of :focus.
  "focus:bg-menu-item-hover data-highlighted:bg-menu-item-hover rounded-lg data-[inset=true]:ml-5 [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-pointer outline-hidden select-none data-disabled:cursor-not-allowed data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 data-[selected=true]:bg-menu-item-selected hover:bg-menu-item-hover data-popup-open:bg-menu-item-hover data-open:bg-menu-item-hover w-full min-w-0",
  {
    variants: {
      size: {
        sm: "h-6 px-2 py-0.5 gap-1.5 items-center",
        base: "h-8 px-2 py-1 gap-1.5 items-center",
        lg: "p-2 gap-2 items-start",
      },
      variant: {
        default:
          "focus:text-default focus:**:text-default data-highlighted:text-default data-highlighted:**:text-default",
        destructive:
          "text-rose focus:text-rose focus:bg-rose-soft focus:**:text-rose focus:**:[svg]:text-rose hover:bg-rose-soft data-highlighted:text-rose data-highlighted:bg-rose-soft data-highlighted:**:text-rose data-highlighted:**:[svg]:text-rose",
      },
    },
    defaultVariants: {
      size: "base",
      variant: "default",
    },
  },
);

function ListItemLabel({
  children,
  size,
}: React.PropsWithChildren<{
  size: VariantProps<typeof listItemVariants>["size"];
}>) {
  return (
    <div
      data-slot="list-item-label"
      className={cn(
        "truncate max-w-full",
        size === "lg" && "text-xs font-medium",
        size === "base" && "text-sm",
        size === "sm" && "text-xs",
      )}
    >
      {children}
    </div>
  );
}

function ListItemDescription({ children }: React.PropsWithChildren) {
  return (
    <div
      data-slot="list-item-description"
      className="min-w-0 line-clamp-2 w-full truncate break-words text-left text-xs text-muted"
    >
      {children}
    </div>
  );
}

interface ListItemProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "children" | "ref"> {
  // eslint-disable-next-line replo/no-jsx-element-property-type -- cloneElement/Radix render requires ReactElement (Noah, 2026-06-26)
  render?: React.ReactElement;
  selected?: boolean;
  disabled?: boolean;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  inset?: boolean;
  variant?: VariantProps<typeof listItemVariants>["variant"];
  size?: VariantProps<typeof listItemVariants>["size"];
  label: React.ReactNode;
  description?: React.ReactNode;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
}

const ListItemInner = <ClassName extends string>(
  {
    render,
    selected,
    disabled,
    className,
    UNSAFE_className,
    inset,
    variant = "default",
    size = "base",
    label,
    description,
    startEnhancer,
    endEnhancer,
    ...props
  }: ListItemProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  return useRender({
    render,
    ref,
    defaultTagName: "div",
    props: {
      ...mergeProps<"div">(
        {
          className: cn(
            listItemVariants({ size, variant }),
            className,
            UNSAFE_className,
          ),
          children: (
            <>
              {startEnhancer}

              <div
                data-slot="list-item-content"
                className="flex flex-col justify-between items-start min-w-0 grow leading-none"
              >
                <ListItemLabel size={size}>{label}</ListItemLabel>
                {description && size === "lg" && (
                  <ListItemDescription>{description}</ListItemDescription>
                )}
              </div>

              {endEnhancer && (
                <span
                  data-slot="list-item-end-enhancer"
                  className="ml-auto flex shrink-0 items-center min-h-full"
                >
                  {endEnhancer}
                </span>
              )}
            </>
          ),
        },
        props,
      ),
      "data-inset": inset,
      "data-variant": variant,
      "data-selected": selected,
      "data-disabled": disabled || undefined,
    },
  });
};

// NOTE (Ben, 2026-02-12): Type cast to preserve generic type parameter for className prop
// See: https://fettblog.eu/typescript-react-generic-forward-refs/
// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const ListItem = React.forwardRef(ListItemInner) as <ClassName extends string>(
  props: ListItemProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

type ListItemContentProps = Pick<
  ListItemProps,
  | "startEnhancer"
  | "endEnhancer"
  | "size"
  | "description"
  | "selected"
  | "inset"
  | "variant"
  | "label"
>;

export { ListItem };
export type { ListItemContentProps };
